import React, { useState, useRef, useMemo } from 'react';
import { Plus, ChevronRight, ChevronLeft, Upload, X, User, FileText, Edit2, Copy, Check, RefreshCw } from 'lucide-react';
import { Card, SectionHeader, Btn, Badge, Modal, Field, Input, ModalRow, EmptyState, Table, TR, TD, StatusBadge, DaysOffChoices } from '../components/UI';
import { fmtAmt, ordinal } from '../utils/helpers';
import { getWorkingDays, getWorkingDaysExHolidays, toMins, fmtMins, calcHolidayUnits, DEFAULT_WORK_HOURS, calcPayroll, defaultAdminPayKey, leaveBalance, nextPayKey, prevPayKey, upcomingPayKey, payPeriod, yearRecord, dailyMinsOf, fmtDate, fmtPeriod, PERIOD_STATUS, flagText, FLAG_NEEDS_ACTION, onPayroll } from '../utils/hr';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { generateUsername, generatePassword, isDefaultWFH, isWFHDay, getDefaultWFHDays, nowPKTDate, manageStaffLogin } from '../utils/auth';
import { EMPLOYEE_ROLES, ROLE_DEFAULT_PERMISSIONS, CLIENT_AD_TYPES } from '../utils/storage';
import { setTimeEntry as setTimeEntryRPC } from '../utils/db';
import { celebrate } from '../utils/celebrate';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DOW_LABELS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

function ym(y,m){ return `${y}-${String(m+1).padStart(2,'0')}`; }
function getWeekDates(refDate) {
  const d = new Date(refDate+'T12:00:00');
  const dow = d.getDay();
  const monday = new Date(d); monday.setDate(d.getDate()-(dow===0?6:dow-1));
  return [0,1,2,3,4].map(i=>{ const x=new Date(monday); x.setDate(monday.getDate()+i); return `${x.getFullYear()}-${String(x.getMonth()+1).padStart(2,'0')}-${String(x.getDate()).padStart(2,'0')}`; });
}

export default function HRPage({ store, persist, showToast }) {
  const { employees=[] } = store;
  const hr    = store.hr||{};
  const creds = store.employeeCredentials||{};
  const leaves = store.leaveRequests||[];

  const now = new Date();
  const [view,         setView]        = useState('list');
  const [selectedEmp,  setSelectedEmp] = useState(null);
  const [profileTab,   setProfileTab]  = useState('overview');
  const [payKeyByEmp,  setPayKeyByEmp] = useState({});        // payroll period picked per employee
  const [recordYear,   setRecordYear]  = useState(() => Number(nowPKTDate().slice(0, 4)));
  // Every employee's year record, built once per store change — shared by the team
  // summary on the list and the Year record tab, so they can never disagree.
  const teamRecords = useMemo(() => {
    const t = nowPKTDate();
    return Object.fromEntries((store.employees || []).filter(e => onPayroll(store, e)).map(e => [e.id, yearRecord(store, e, recordYear, t)]));
  }, [store, recordYear]);
  const [showEmpModal, setShowEmpModal]= useState(false);
  const [editingEmp,   setEditingEmp]  = useState(null);
  const [empForm,      setEmpForm]     = useState({});
  const [newCredModal, setNewCredModal]= useState(null); // {name,username,password}
  const [copiedField,  setCopiedField] = useState('');
  const [calYear,      setCalYear]     = useState(now.getFullYear());
  const [calMonth,     setCalMonth]    = useState(now.getMonth());
  const [leaveTab,     setLeaveTab]    = useState('pending'); // pending|all
  const photoRef = useRef(); const idRef = useRef(); const conRef = useRef();

  function getHR(id) { return hr[id]||{}; }
  function setHR(id, patch) { persist({...store, hr:{...hr,[id]:{...getHR(id),...patch}}}); }

  // ── Employee CRUD ─────────────────────────────────────────────────
  function openAdd() {
    setEditingEmp(null);
    setEmpForm({name:'',amount:'',dueDay:'',joiningDate:'',workStart:'09:00',workEnd:'18:00',role:'Content Manager',noPayroll:false});
    setShowEmpModal(true);
  }
  function openEdit(emp) {
    const h = getHR(emp.id);
    setEditingEmp(emp);
    setEmpForm({name:emp.name,amount:emp.amount,dueDay:emp.dueDay,joiningDate:h.joiningDate||'',
      workStart:h.workHours?.start||DEFAULT_WORK_HOURS[emp.id]?.start||'09:00',
      workEnd:h.workHours?.end||DEFAULT_WORK_HOURS[emp.id]?.end||'18:00',
      role:emp.role||'Content Manager', noPayroll:!!h.noPayroll,
      // What was in force before this edit — lets a change be dated instead of silently
      // rewriting every past period (salary and hours are read per pay period).
      _origAmount:Number(emp.amount)||0,
      _origStart:h.workHours?.start||DEFAULT_WORK_HOURS[emp.id]?.start||'09:00',
      _origEnd:h.workHours?.end||DEFAULT_WORK_HOURS[emp.id]?.end||'18:00',
      _fromKey:upcomingPayKey(emp, nowPKTDate()),
      _fromLabel:fmtDate(payPeriod(emp, upcomingPayKey(emp, nowPKTDate())).dueDate),
      effective:'upcoming'});
    setShowEmpModal(true);
  }
  // Record a change in a per-period history. 'upcoming' keeps every past period at the
  // value agreed then and applies the new one from the next salary; 'all' treats the old
  // value as a mistake and replaces it everywhere.
  function withHistory(list, fromKey, entry, oldEntry, mode) {
    if (mode === 'all') return [{ from:'2000-01', ...entry }];
    const base = Array.isArray(list) && list.length ? list : [{ from:'2000-01', ...oldEntry }];
    return [...base.filter(x => x && x.from < fromKey), { from: fromKey, ...entry }];
  }
  async function saveEmp() {
    const {joiningDate,workStart,workEnd,role} = empForm;
    const noPayroll = !!empForm.noPayroll;
    const name = empForm.name;
    // No working hours or salary (an agent / freelancer): login + work access only.
    const amount = noPayroll ? 0 : empForm.amount;
    const dueDay = noPayroll ? 1 : empForm.dueDay;
    if (!name) return showToast('Name required.','error');
    if (!noPayroll && (!amount||!dueDay)) return showToast('Name, salary and due day required.','error');
    const updated = {...store, employees:[...employees]};
    if (editingEmp) {
      const defPerms = ROLE_DEFAULT_PERMISSIONS[role]||{socialMedia:false,perfMarketing:false};
      updated.employees = updated.employees.map(e=>e.id===editingEmp.id?{...e,name,amount:Number(amount),dueDay:Number(dueDay),role,reportPerms:e.reportPerms||defPerms}:e);
      const prevHR = getHR(editingEmp.id);
      const patch  = {...prevHR,joiningDate,workHours:{start:workStart,end:workEnd},noPayroll};
      const mode   = empForm.effective||'upcoming';
      if (!noPayroll && Number(amount) !== empForm._origAmount)
        patch.salaryHistory = withHistory(prevHR.salaryHistory, empForm._fromKey, {amount:Number(amount)}, {amount:empForm._origAmount}, mode);
      if (!noPayroll && (workStart !== empForm._origStart || workEnd !== empForm._origEnd))
        patch.workHoursHistory = withHistory(prevHR.workHoursHistory, empForm._fromKey, {start:workStart,end:workEnd}, {start:empForm._origStart,end:empForm._origEnd}, mode);
      updated.hr = {...hr,[editingEmp.id]:patch};
      showToast('Employee updated.');
      persist(updated); setShowEmpModal(false);
      if (selectedEmp?.id===editingEmp.id) setSelectedEmp(updated.employees.find(e=>e.id===editingEmp.id));
    } else {
      // New employee — create their sign-in first (Supabase Auth, via the admin-only
      // staff-auth function). Only the username is kept in the app; the password is
      // shown once, below, and never stored.
      const newId    = 'emp_'+Date.now();
      const username = generateUsername(name);
      const password = generatePassword(12);
      try { await manageStaffLogin({ action:'upsert', empId:newId, username, password, name }); }
      catch (err) { showToast(err.message, 'error'); return; }
      const newPerms = ROLE_DEFAULT_PERMISSIONS[role]||{socialMedia:false,perfMarketing:false};
      updated.employees.push({id:newId,name,amount:Number(amount),dueDay:Number(dueDay),type:'fixed',role,reportPerms:newPerms});
      updated.hr = {...hr,[newId]: noPayroll ? {joiningDate,noPayroll:true} : {joiningDate,workHours:{start:workStart,end:workEnd}}};
      updated.employeeCredentials = {...creds,[newId]:{username}};
      persist(updated);
      setShowEmpModal(false);
      setNewCredModal({name,username,password}); // show credentials modal
    }
  }
  // Give an employee a fresh password (creating their sign-in if they don't have one).
  // The password is shown once in the credentials window and isn't kept anywhere.
  async function issuePassword(emp, existingUsername) {
    const username = existingUsername || generateUsername(emp.name);
    const password = generatePassword(12);
    try { await manageStaffLogin({ action:'upsert', empId:emp.id, username, password, name:emp.name }); }
    catch (err) { showToast(err.message, 'error'); return; }
    if (!existingUsername) persist({...store, employeeCredentials:{...creds,[emp.id]:{username}}});
    setNewCredModal({ name:emp.name, username, password, reset:!!existingUsername });
  }
  function deleteEmp(id) {
    if (!window.confirm('Remove this employee and all their data?')) return;
    const updated = {...store, employees:employees.filter(e=>e.id!==id)};
    const newHr={...hr}; delete newHr[id];
    const newCreds={...creds}; delete newCreds[id];
    updated.hr=newHr; updated.employeeCredentials=newCreds;
    persist(updated, { deletedIds: { employees: [id] } });
    if (selectedEmp?.id===id){setView('list');setSelectedEmp(null);}
    showToast('Employee removed.');
    manageStaffLogin({ action:'remove', empId:id })
      .catch(() => showToast("Employee removed, but their sign-in couldn't be switched off — try again or remove it in Supabase.", 'error'));
  }

  // ── File uploads ──────────────────────────────────────────────────
  function handleFile(empId,field,file) {
    if (!file) return;
    if (file.size>5*1024*1024) return showToast('Max 5MB.','error');
    const r=new FileReader(); r.onload=e=>{setHR(empId,{[field]:{name:file.name,data:e.target.result,type:file.type}});showToast('File uploaded.');};
    r.readAsDataURL(file);
  }
  function removeFile(empId,field) {
    const h={...getHR(empId)}; delete h[field];
    persist({...store,hr:{...hr,[empId]:h}}); showToast('File removed.');
  }

  // ── Attendance helpers ────────────────────────────────────────────
  const ymStr = ym(calYear,calMonth);
  const allCalDays  = getWorkingDays(ymStr); // all Mon-Fri (for attendance grid)
  const workingDays = getWorkingDaysExHolidays(ymStr, store.holidays||[]); // excludes public holidays
  function getAtt(id)  { return getHR(id).attendance||{}; }
  function getTL(id)   { return getHR(id).timelog||{}; }
  function cycleAtt(empId,date) {
    const cur=getAtt(empId)[date]||'';
    const cycle=['','present','half','absent','leave'];
    const next=cycle[(cycle.indexOf(cur)+1)%cycle.length];
    setHR(empId,{attendance:{...getAtt(empId),[date]:next}});
  }
  function setTimeEntry(empId,date,field,val) {
    const tl={...getTL(empId),[date]:{...(getTL(empId)[date]||{}),[field]:val}};
    setHR(empId,{timelog:tl});                 // optimistic local UI
    // Durable write: the whole-store save now preserves the DB timelog, so admin
    // corrections must go through the targeted merge RPC to actually persist.
    setTimeEntryRPC(empId,date,field,val).catch(()=>showToast('Could not save time edit — try again.','error'));
  }

  // ── Report permissions toggle ─────────────────────────────────────
  function toggleReportPerm(empId, permKey) {
    const emp = employees.find(e=>e.id===empId);
    if (!emp) return;
    const current = emp.reportPerms || { socialMedia:false, perfMarketing:false };
    const updated = {
      ...store,
      employees: employees.map(e => e.id===empId
        ? { ...e, reportPerms: { ...current, [permKey]: !current[permKey] } }
        : e
      )
    };
    persist(updated);
    if (selectedEmp?.id===empId) setSelectedEmp(updated.employees.find(e=>e.id===empId));
  }

  // ── Overtime request management ──────────────────────────────────
  function resolveOvertime(reqId, status) {
    if (status==='approved') celebrate('approve');
    const updated = {
      ...store,
      overtimeRequests: (store.overtimeRequests||[]).map(r =>
        r.id===reqId ? {...r, status, resolvedAt: new Date().toISOString()} : r
      )
    };
    persist(updated);
    showToast(status==='approved'?'Overtime approved.':'Overtime rejected.');
  }

  // ── WFH weekly toggles ────────────────────────────────────────────
  const todayStr  = nowPKTDate();
  const weekDates = getWeekDates(todayStr);
  function toggleWFH(empId,date) {
    const overrides = getHR(empId).wfhOverrides||{};
    const isDefault = isDefaultWFH(empId,date,hr);
    const cur = overrides[date]!==undefined ? overrides[date] : (isDefault?'wfh':'office');
    const next = cur==='wfh'?'office':'wfh';
    setHR(empId,{wfhOverrides:{...overrides,[date]:next}});
  }

  // Toggle a permanent WFH day for an employee
  function toggleDefaultWFHDay(empId, dow) {
    const current = getDefaultWFHDays(empId, hr);
    const updated  = current.includes(dow)
      ? current.filter(d => d !== dow)
      : [...current, dow].sort((a,b)=>a-b);
    setHR(empId, { wfhDefaultDays: updated });
    showToast('Default WFH schedule updated.');
  }
  function isWFHForDate(empId,date) { return isWFHDay(empId,date,hr); }

  // ── Leave request admin actions ───────────────────────────────────
  // Approving just marks the day as leave. Approved or not, a day off comes out of the
  // paid days off allowance (see utils/hr.js); only days beyond it cost salary, and the
  // per-period waive in the Payroll tab can still override that.
  function handleLeave(id,action) {
    if (action==='approved') celebrate('leave');
    const newAtt = action==='approved'?'leave':'absent';
    let updated = {...store, leaveRequests:leaves.map(r=>{
      if (r.id!==id) return r;
      return {...r,status:action};
    })};
    // Update attendance if approved/rejected
    const req = leaves.find(r=>r.id===id);
    if (req) {
      const empHR = getHR(req.empId);
      updated.hr = {...hr,[req.empId]:{...empHR,attendance:{...empHR.attendance||{},[req.date]:newAtt}}};
    }
    persist(updated);
    showToast(`Leave ${action}.`);
  }

  // ── Salary calc (pay-period engine, see utils/hr.js) ──────────────
  // A period is keyed by the month its salary falls due ('2026-10' = paid on the
  // due day in October), matching salary_payments.monthYear. With no period
  // given, the admin lands on the payday that just passed if it is unpaid,
  // otherwise the next one.
  const today = nowPKTDate();
  function getSalaryCalc(emp, payKey) {
    return calcPayroll(store, emp, payKey || defaultAdminPayKey(store, emp, today), today);
  }

  // ── Record an employee's / admin's monthly payroll decision ───────
  function setPayrollDecision(empId, monthYm, patch) {
    const h = getHR(empId);
    const payroll = { ...(h.payroll || {}) };
    payroll[monthYm] = { ...(payroll[monthYm] || {}), ...patch };
    setHR(empId, { payroll });
  }

  // ── How a day off is paid: allowance or salary cut (employee's choice; admin can set it) ──
  function setOffChoice(empId, date, value) {
    const h = getHR(empId);
    setHR(empId, { offChoice: { ...(h.offChoice || {}), [date]: value } });
  }

  // ── Copy helper ───────────────────────────────────────────────────
  function copyText(text,field) {
    navigator.clipboard.writeText(text).then(()=>{setCopiedField(field);setTimeout(()=>setCopiedField(''),2000);});
  }

  // ── Profile view ──────────────────────────────────────────────────
  if (view==='profile'&&selectedEmp) {
    const emp  = employees.find(e=>e.id===selectedEmp.id)||selectedEmp;
    const h    = getHR(emp.id);
    const wh   = h.workHours||DEFAULT_WORK_HOURS[emp.id]||{start:'09:00',end:'18:00'};
    const payKey = payKeyByEmp[emp.id] || defaultAdminPayKey(store, emp, today);
    const calc = getSalaryCalc(emp, payKey);
    const bank = leaveBalance(store, emp, today);
    const setPayKey = k => setPayKeyByEmp(m => ({ ...m, [emp.id]: k }));
    const tl   = getTL(emp.id);
    const empCred = creds[emp.id];
    const empLeaves = leaves.filter(r=>r.empId===emp.id);

    const NO_PAY = !onPayroll(store, emp);
    const PROFILE_TABS = (NO_PAY ? [{id:'overview',label:'Overview'},{id:'permissions',label:'Permissions'},{id:'creds',label:'Credentials'}] : null) || [{id:'overview',label:'Overview'},{id:'record',label:'Year record'},{id:'attendance',label:'Attendance'},{id:'timelog',label:'Time log'},{id:'payroll',label:'Payroll'},{id:'overtime',label:'Overtime'},{id:'permissions',label:'Permissions'},{id:'creds',label:'Credentials'}];

    return (
      <div style={{display:'flex',flexDirection:'column',gap:16}}>
        <div style={{display:'flex',alignItems:'center',gap:12}}>
          <Btn size="sm" variant="ghost" onClick={()=>{setView('list');setSelectedEmp(null);}}>
            <ChevronLeft size={14}/>All employees
          </Btn>
        </div>

        <Card>
          {/* Profile header */}
          <div style={{padding:'1.25rem 1.5rem',display:'flex',alignItems:'flex-start',gap:18,flexWrap:'wrap'}}>
            {/* Photo */}
            <div style={{position:'relative',flexShrink:0}}>
              <div style={{width:76,height:76,borderRadius:'50%',background:'var(--bg4)',border:'2px solid var(--border2)',overflow:'hidden',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer'}} onClick={()=>photoRef.current?.click()}>
                {h.photo?<img src={h.photo.data} alt="photo" style={{width:'100%',height:'100%',objectFit:'cover'}}/>:<User size={26} color="var(--text3)"/>}
              </div>
              <input ref={photoRef} type="file" accept="image/*" style={{display:'none'}} onChange={e=>handleFile(emp.id,'photo',e.target.files[0])}/>
              <div style={{position:'absolute',bottom:0,right:0,background:'var(--accent)',borderRadius:'50%',width:22,height:22,display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer'}} onClick={()=>photoRef.current?.click()}>
                <Upload size={11} color="#000"/>
              </div>
            </div>
            {/* Info */}
            <div style={{flex:1,minWidth:0}}>
              <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4,flexWrap:'wrap'}}>
                <h2 style={{margin:0,fontSize:23,fontFamily:'var(--serif)',fontWeight:400,color:'var(--text)'}}>{emp.name}</h2>
                <Btn size="sm" variant="ghost" onClick={()=>openEdit(emp)}><Edit2 size={11}/>Edit</Btn>
                <Btn size="sm" variant="danger" onClick={()=>deleteEmp(emp.id)}>Remove</Btn>
              </div>
              <div style={{marginBottom:6}}>
                <span style={{fontSize:11,fontWeight:600,padding:'2px 10px',borderRadius:'var(--radius)',background:'rgba(169,137,83,0.12)',color:'var(--accent)'}}>{emp.role||'Staff'}</span>
              </div>
              <div style={{display:'flex',gap:16,flexWrap:'wrap',fontSize:13,color:'var(--text3)'}}>
                {NO_PAY ? <span>No working hours or salary</span> : <>
                <span>Salary: <strong style={{color:'var(--text)',fontFamily:'var(--mono)',fontVariantNumeric:'lining-nums tabular-nums'}}>{fmtAmt(emp.amount)}</strong></span>
                <span>Due: <strong style={{color:'var(--text)',fontFamily:'var(--mono)',fontVariantNumeric:'lining-nums tabular-nums'}}>{ordinal(emp.dueDay)}</strong></span>
                <span>Hours: <strong style={{color:'var(--text)',fontFamily:'var(--mono)',fontVariantNumeric:'lining-nums tabular-nums'}}>{fmtMins(dailyMinsOf(wh))} a day</strong> <span style={{color:'var(--text3)'}}>(usually {wh.start}–{wh.end})</span></span>
                </>}
                {h.joiningDate&&<span>Joined: <strong style={{color:'var(--text)',fontFamily:'var(--mono)',fontVariantNumeric:'lining-nums tabular-nums'}}>{h.joiningDate}</strong></span>}
              </div>
            </div>
            {/* Documents */}
            <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
              {[['idCard','ID Card',idRef,'var(--blue)'],['contract','Contract',conRef,'#8FB08A']].map(([field,label,ref,col])=>(
                <div key={field}>
                  <input ref={ref} type="file" accept="image/*,.pdf" style={{display:'none'}} onChange={e=>handleFile(emp.id,field,e.target.files[0])}/>
                  {h[field]?(
                    <div style={{display:'flex',alignItems:'center',gap:5,background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:'var(--radius)',padding:'5px 10px',fontSize:12}}>
                      <FileText size={12} color={col}/>
                      <span style={{color:'var(--text2)',maxWidth:90,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{h[field].name}</span>
                      <button onClick={()=>removeFile(emp.id,field)} style={{background:'none',border:'none',cursor:'pointer',color:'var(--text3)',display:'flex',padding:0}}><X size={11}/></button>
                    </div>
                  ):<Btn size="sm" variant="ghost" onClick={()=>ref.current?.click()}><Upload size={11}/>{label}</Btn>}
                </div>
              ))}
            </div>
          </div>

          {/* Tabs */}
          <div className="segmented" style={{display:'flex',gap:2,overflowX:'auto',margin:'4px 0 8px',maxWidth:'100%'}}>
            {PROFILE_TABS.map(t=>(
              <button key={t.id} onClick={()=>setProfileTab(t.id)} style={{padding:'9px 15px',fontSize:13,fontWeight:600,cursor:'pointer',border:'none',borderRadius:'var(--radius)',background:profileTab===t.id?'var(--bg4)':'transparent',whiteSpace:'nowrap',color:profileTab===t.id?'var(--text)':'var(--text3)',boxShadow:'none',transition:'all 0.15s'}}>{t.label}</button>
            ))}
          </div>

          {/* ── OVERVIEW ── */}
          {profileTab==='overview'&&NO_PAY&&(
            <div className="page-anim" style={{padding:'1.25rem 1.5rem'}}>
              <div style={{background:'var(--bg3)',borderRadius:'var(--radius)',padding:'14px 16px',fontSize:13,color:'var(--text2)'}}>
                <strong style={{color:'var(--text)'}}>No working hours or salary.</strong> {emp.name} has a login and work access as {emp.role||'staff'}, and is left out of payroll, attendance, hours and payday reminders. Change this with Edit.
              </div>
            </div>
          )}
          {profileTab==='overview'&&!NO_PAY&&(
            <div className="page-anim" style={{padding:'1.25rem 1.5rem'}}>
              <div className="grid-overview" style={{marginBottom:14}}>
                {[
                  {label:'Base salary',value:fmtAmt(emp.amount)},
                  {label:`Shortfall · ${fmtPeriod(calc.period)}`,value:calc.shortfallMins>0?fmtMins(calc.shortfallMins):'None',color:calc.shortfallMins>0?'#C79A5B':'#8FB08A'},
                  {label:'Paid days off left',value:`${bank.remaining}`,color:bank.remaining>0?'var(--blue)':'var(--text3)'},
                  {label:`Salary due ${fmtDate(calc.period.dueDate)}`,value:fmtAmt(calc.finalSalary),color:calc.finalDed>0?'#C08079':'#8FB08A'},
                ].map(c=>(
                  <div key={c.label} style={{background:'var(--bg3)',borderRadius:'var(--radius)',padding:'0.875rem 1rem'}}>
                    <div style={{fontSize:11,color:'var(--text3)',marginBottom:4,fontWeight:400,fontFamily:'var(--mono)',textTransform:'uppercase',letterSpacing:'0.1em'}}>{c.label}</div>
                    <div style={{fontSize:17,fontWeight:700,color:c.color||'var(--text)',fontFamily:'var(--mono)',fontVariantNumeric:'lining-nums tabular-nums'}}>{c.value}</div>
                  </div>
                ))}
              </div>

              {/* WFH this week */}
              <div style={{marginTop:16}}>
                {/* Paid days off bank (Aug–Jul year) */}
                {(() => {
                  const pto = bank;
                  return (
                    <>
                    <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,marginBottom:14}}>
                      {[
                        {label:'Accrued',   value:`${pto.accrued} days`,  color:'var(--blue)'},
                        {label:'Used',      value:`${pto.used} days`,     color:'#C79A5B'},
                        {label:'Remaining', value:`${pto.remaining} days`,color:pto.remaining>0?'#8FB08A':'#C08079'},
                      ].map(({label,value,color})=>(
                        <div key={label} style={{background:'var(--bg3)',borderRadius:'var(--radius)',padding:'12px 14px',border:'1px solid var(--border)'}}>
                          <div style={{fontSize:10,fontWeight:400,fontFamily:'var(--mono)',color:'var(--text3)',textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:6}}>{label}</div>
                          <div style={{fontSize:18,fontWeight:700,color,fontFamily:'var(--mono)',fontVariantNumeric:'lining-nums tabular-nums'}}>{value}</div>
                          <div style={{fontSize:10,color:'var(--text3)',marginTop:2}}>Paid days off · since {fmtDate(pto.leaveYear)}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{marginBottom:20,display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
                      <span style={{fontSize:12,color:'var(--text3)'}}>Days off taken before app / last year:</span>
                      <input type="number" min="0" step="0.5"
                        value={getHR(emp.id).manualLeaveAdj||0}
                        onChange={e=>setHR(emp.id,{manualLeaveAdj:Number(e.target.value)})}
                        style={{width:70,padding:'4px 8px',fontSize:13,background:'var(--bg3)',border:'1px solid var(--border2)',borderRadius:'var(--radius)',color:'var(--text)',textAlign:'center'}}
                      />
                      <span style={{fontSize:11,color:'var(--text3)'}}>days (subtracted from the bank)</span>
                    </div>
                    </>
                  );
                })()}
                <div style={{fontSize:12,fontWeight:400,fontFamily:'var(--mono)',color:'var(--text3)',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:8}}>Default WFH days (permanent)</div>
                <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:16}}>
                  {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map((day,dow)=>{
                    const isDefault = getDefaultWFHDays(emp.id, hr).includes(dow);
                    return (
                      <button key={dow} onClick={()=>toggleDefaultWFHDay(emp.id,dow)}
                        style={{display:'flex',flexDirection:'column',alignItems:'center',padding:'8px 12px',borderRadius:'var(--radius)',
                          border:`1px solid ${isDefault?'#8FB08A':'var(--border2)'}`,
                          background:isDefault?'rgba(143,176,138,0.1)':'var(--bg3)',
                          cursor:'pointer',transition:'all 0.15s',minWidth:52}}>
                        <span style={{fontSize:11,fontWeight:600,color:'var(--text2)'}}>{day}</span>
                        <span style={{fontSize:10,marginTop:3,fontWeight:600,color:isDefault?'#8FB08A':'var(--text3)'}}>{isDefault?'WFH':'Office'}</span>
                      </button>
                    );
                  })}
                </div>
                <div style={{fontSize:12,fontWeight:400,fontFamily:'var(--mono)',color:'var(--text3)',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:8}}>WFH schedule — this week</div>
                <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                  {weekDates.map(date=>{
                    const dow=new Date(date+'T12:00:00').getDay();
                    const isWFH=isWFHForDate(emp.id,date);
                    const isOverridden=(getHR(emp.id).wfhOverrides||{})[date]!==undefined;
                    return (
                      <button key={date} onClick={()=>toggleWFH(emp.id,date)} style={{display:'flex',flexDirection:'column',alignItems:'center',padding:'8px 12px',borderRadius:'var(--radius)',border:`1px solid ${isWFH?'var(--blue)':'var(--border2)'}`,background:isWFH?'var(--blue-bg)':'var(--bg3)',cursor:'pointer',transition:'all 0.15s',minWidth:56}}>
                        <span style={{fontSize:11,fontWeight:600,color:'var(--text3)'}}>{DOW_LABELS[dow]}</span>
                        <span style={{fontSize:10,marginTop:3,fontWeight:600,color:isWFH?'var(--blue)':'var(--text3)'}}>{isWFH?'WFH':'Office'}</span>
                        {isOverridden&&<span style={{fontSize:9,color:'#C79A5B',marginTop:2}}>override</span>}
                      </button>
                    );
                  })}
                </div>
                <p style={{fontSize:11,color:'var(--text3)',marginTop:8}}>Click any day to toggle. Override resets to default next week.</p>
              </div>
            </div>
          )}

          {/* ── YEAR RECORD ── */}
          {profileTab==='record'&&(()=>{
            const Y = teamRecords[emp.id];
            if (!Y) return null;
            const T = Y.totals, A = T.avg;
            const hrs = m => fmtMins(Math.abs(m)) === '—' ? '0h' : fmtMins(Math.abs(m));
            const signed = m => m===0 ? 'Even' : (m>0?'+':'−')+hrs(m);
            const money = n => n==null ? '—' : fmtAmt(n);
            const track = Y.rows.find(r=>r.trackingStart)?.trackingStart;
            const cell = {padding:'9px 10px',borderBottom:'1px solid var(--border)',fontFamily:'var(--mono)',fontSize:12,fontVariantNumeric:'lining-nums tabular-nums',whiteSpace:'nowrap',textAlign:'right'};
            const head = {...cell,fontSize:9.5,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'0.08em',fontWeight:400,position:'sticky',top:0,background:'var(--bg2)'};
            const numIn = {width:56,padding:'4px 6px',fontSize:12,background:'var(--bg3)',border:'1px solid var(--border2)',borderRadius:'var(--radius)',color:'var(--text)',textAlign:'center',fontFamily:'var(--mono)'};
            const chart = Y.rows.filter(r=>r.started && r.tracked).map(r=>({m:MONTHS[Number(r.month.slice(5))-1].slice(0,3), worked:Math.round(r.hoursWorkedMins/60), expected:Math.round(r.requiredMins/60)}));
            const cards = [
              {k:'Hours a month', v:`${hrs(A.hoursWorkedMins)} of ${hrs(A.requiredMins)}`, s:`worked of required (holidays and days off not required) · ${hrs(T.hoursExtraMins)} extra on record (not carried over)`, c:A.hoursWorkedMins<A.requiredMins?'#C79A5B':'#8FB08A'},
              {k:'Days off', v:`${T.daysOff}${T.daysOffUnpaid?` · ${T.daysOffUnpaid} unpaid`:''}`, s:`${A.daysOff} a month · ${T.bank.remaining} of ${T.bank.accrued} paid days off left`, c:T.daysOffUnpaid?'#C08079':'var(--text)'},
              {k:'Average day', v:A.hoursPerDay?hrs(A.hoursPerDay):'—', s:`required ${fmtMins(calc.dailyMins)} a day · start time doesn't matter`, c:A.hoursPerDay&&A.hoursPerDay<calc.dailyMins?'#C79A5B':'#8FB08A'},
              {k:'Should have been cut', v:fmtAmt(T.shouldCut), s:`${fmtAmt(A.shouldCut)} a month on average${T.waived?` · ${fmtAmt(T.waived)} waived`:''}`, c:T.shouldCut?'#C08079':'#8FB08A'},
              {k:'Paid vs should have', v:T.paidPeriods?(T.overpaid===0?'Matches':(T.overpaid>0?`${fmtAmt(T.overpaid)} over`:`${fmtAmt(-T.overpaid)} under`)):'—', s:T.paidPeriods?`${fmtAmt(T.paid)} paid across ${T.paidPeriods} salar${T.paidPeriods===1?'y':'ies'}`:'No salaries recorded yet', c:T.overpaid>0?'#C08079':'#8FB08A'},
              {k:'Overtime owed', v:T.overtimePay?fmtAmt(T.overtimePay):'—', s:T.overtimeMins?`${hrs(T.overtimeMins)} beyond the schedule, at 1.5×`:'only hours beyond a month’s schedule count', c:T.overtimePay?'var(--accent)':'var(--text3)'},
              {k:'Forgotten punches', v:`${T.forgottenPunches}`, s:'closed at shift end, each flagged', c:T.forgottenPunches?'#C79A5B':'#8FB08A'},
            ];
            return (
            <div className="page-anim" style={{padding:'1.25rem 1.5rem'}}>
              <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:6,flexWrap:'wrap'}}>
                <Btn size="sm" variant="ghost" onClick={()=>setRecordYear(y=>y-1)}><ChevronLeft size={13}/></Btn>
                <div style={{fontSize:16,fontWeight:600,color:'var(--text)',fontFamily:'var(--mono)'}}>{Y.year}</div>
                <Btn size="sm" variant="ghost" onClick={()=>setRecordYear(y=>y+1)}><ChevronRight size={13}/></Btn>
                <span style={{fontSize:12,color:'var(--text3)'}}>Leave allowance runs 1 Jan – 31 Dec · 1.5 days a month, up to 18</span>
              </div>
              {track && <p style={{fontSize:12,color:'var(--text3)',margin:'0 0 16px'}}>Clock-ins in the app start {fmtDate(track)}. For earlier months, type the days off taken in the table — they come off the leave allowance. Figures from the attendance grid are pre-filled.</p>}

              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(190px,1fr))',gap:10,marginBottom:20}}>
                {cards.map(c=>(
                  <div key={c.k} style={{background:'var(--bg3)',borderRadius:'var(--radius)',padding:'12px 14px',border:'1px solid var(--border)'}}>
                    <div style={{fontSize:10,fontFamily:'var(--mono)',color:'var(--text3)',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:6}}>{c.k}</div>
                    <div style={{fontSize:18,fontWeight:700,color:c.c,fontFamily:'var(--mono)',fontVariantNumeric:'lining-nums tabular-nums'}}>{c.v}</div>
                    <div style={{fontSize:11,color:'var(--text3)',marginTop:3}}>{c.s}</div>
                  </div>
                ))}
              </div>

              {chart.length>0 && (
                <div style={{height:190,marginBottom:20}}>
                  <div style={{fontSize:12,color:'var(--text3)',marginBottom:6}}>Hours credited against hours expected, by month</div>
                  <ResponsiveContainer width="100%" height="88%">
                    <BarChart data={chart} margin={{top:4,right:4,bottom:0,left:-18}}>
                      <CartesianGrid stroke="var(--border)" vertical={false}/>
                      <XAxis dataKey="m" tick={{fontSize:11,fill:'var(--text3)'}} axisLine={false} tickLine={false}/>
                      <YAxis tick={{fontSize:11,fill:'var(--text3)'}} axisLine={false} tickLine={false}/>
                      <Tooltip contentStyle={{background:'var(--bg2)',border:'1px solid var(--border)',fontSize:12}} formatter={(v,n)=>[`${v}h`, n==='worked'?'Credited':'Expected']}/>
                      <Bar dataKey="expected" fill="var(--bg4)" radius={[2,2,0,0]} animationDuration={800} animationEasing="ease-out"/>
                      <Bar dataKey="worked" fill="var(--accent)" radius={[2,2,0,0]} animationDuration={1000} animationEasing="ease-out"/>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              <div style={{overflowX:'auto',border:'1px solid var(--border)',borderRadius:'var(--radius)'}}>
                <table style={{width:'100%',borderCollapse:'collapse',minWidth:1000}}>
                  <thead><tr>
                    <th style={{...head,textAlign:'left'}}>Month</th><th style={head}>Hours</th><th style={head}>+ / −</th>
                    <th style={head}>Days off</th><th style={head}>Unpaid</th><th style={head}>Avg day</th>
                    <th style={head}>Salary</th><th style={head}>Should cut</th><th style={head}>Overtime</th><th style={head}>Should pay</th><th style={head}>Paid</th><th style={head}>Difference</th>
                  </tr></thead>
                  <tbody>
                    {Y.rows.map(r=>{
                      const name = MONTHS[Number(r.month.slice(5))-1];
                      const dim  = !r.started ? 0.4 : 1;
                      const pre  = r.untrackedDays>0;
                      const noHours = !r.tracked || !r.started;
                      const diff = r.paid && r.complete ? r.overpaid : null;
                      return (
                        <tr key={r.key} style={{opacity:dim}}>
                          <td style={{...cell,textAlign:'left',fontFamily:'var(--font)'}}>
                            <div style={{fontSize:13,color:'var(--text)'}}>{name}</div>
                            <div style={{fontSize:10,color:'var(--text3)',fontFamily:'var(--mono)'}}>{fmtPeriod(r.period)}{!r.tracked&&r.started?' · before records':''}{r.status==='in_progress'?' · so far':''}</div>
                          </td>
                          <td style={cell} title="Hours worked / hours required (public holidays and days off are not required)">{noHours?'—':`${hrs(r.hoursWorkedMins)} / ${hrs(r.requiredMins)}`}</td>
                          <td style={{...cell,color:noHours?'var(--text3)':r.hoursWorkedMins<r.requiredMins?'#C79A5B':'#8FB08A'}}>{noHours?'—':signed(r.hoursWorkedMins-r.requiredMins)}</td>
                          <td style={cell}>
                            {!r.started ? '—' : pre ? (
                              <span style={{display:'inline-flex',alignItems:'center',gap:6,justifyContent:'flex-end'}}>
                                {r.daysOffTracked>0 && <span style={{color:'var(--text3)'}}>{r.daysOffTracked} +</span>}
                                <input type="number" min="0" step="0.5" style={numIn} title="Days off before records began"
                                  value={r.daysOffOverride ? r.daysOffUntracked : r.untrackedOffMarks}
                                  onChange={e=>setPayrollDecision(emp.id, r.key, {daysOff: e.target.value===''?'':Math.max(0, Math.round(Number(e.target.value)*2)/2), decidedBy:'admin'})}/>
                              </span>
                            ) : r.daysOff}
                          </td>
                          <td style={{...cell,color:r.daysOffUnpaid?'#C08079':'var(--text3)'}}>{!r.started||!r.daysOffUnpaid ? '—' : r.daysOffUnpaid}</td>
                          <td style={{...cell,color:noHours||r.avgDayMins==null?'var(--text3)':r.avgDayMins<r.dailyMins?'#C79A5B':'var(--text)'}}>{noHours||r.avgDayMins==null?'—':hrs(r.avgDayMins)}</td>
                          <td style={cell}>{fmtAmt(r.salary)}</td>
                          <td style={{...cell,color:r.deduction?'#C08079':'var(--text3)'}}>{!r.started?'—':(r.deduction?`− ${fmtAmt(r.deduction)}`:'—')}{r.waive&&r.deduction?' (waived)':''}</td>
                          <td style={{...cell,color:r.overtimePay?'var(--accent)':'var(--text3)'}}>{r.started&&r.overtimePay?`+ ${fmtAmt(r.overtimePay)}`:'—'}</td>
                          <td style={cell}>{!r.started?'—':fmtAmt(r.finalSalary)}</td>
                          <td style={cell}>{money(r.paidAmount)}</td>
                          <td style={{...cell,color:diff==null?'var(--text3)':diff>0?'#C08079':diff<0?'var(--blue)':'#8FB08A'}}>{diff==null?'—':diff===0?'✓':diff>0?`${fmtAmt(diff)} over`:`${fmtAmt(-diff)} under`}</td>
                        </tr>
                      );
                    })}
                    <tr style={{background:'var(--bg3)'}}>
                      <td style={{...cell,textAlign:'left',fontFamily:'var(--font)',fontWeight:600,color:'var(--text)'}}>{Y.year} so far</td>
                      <td style={{...cell,fontWeight:600}}>{`${hrs(T.hoursWorkedMins)} / ${hrs(T.requiredMins)}`}</td>
                      <td style={{...cell,fontWeight:600}} title="Each month balances on its own; extra hours are recorded, not carried over">
                        <span style={{color:'#8FB08A'}}>+{hrs(T.hoursExtraMins)}</span> <span style={{color:'#C79A5B'}}>−{hrs(T.hoursShortMins)}</span>
                      </td>
                      <td style={{...cell,fontWeight:600}}>{T.daysOff}</td>
                      <td style={{...cell,fontWeight:600,color:T.daysOffUnpaid?'#C08079':'var(--text3)'}}>{T.daysOffUnpaid||'—'}</td>
                      <td style={{...cell,fontWeight:600}}>{A.hoursPerDay?hrs(A.hoursPerDay):'—'}</td>
                      <td style={cell}></td>
                      <td style={{...cell,fontWeight:600,color:T.shouldCut?'#C08079':'var(--text3)'}}>{T.shouldCut?`− ${fmtAmt(T.shouldCut)}`:'—'}</td>
                      <td style={{...cell,fontWeight:600,color:T.overtimePay?'var(--accent)':'var(--text3)'}}>{T.overtimePay?`+ ${fmtAmt(T.overtimePay)}`:'—'}</td>
                      <td style={{...cell,fontWeight:600}}>{T.paidPeriods?fmtAmt(T.shouldPay):'—'}</td>
                      <td style={{...cell,fontWeight:600}}>{T.paidPeriods?fmtAmt(T.paid):'—'}</td>
                      <td style={{...cell,fontWeight:600,color:T.overpaid>0?'#C08079':'#8FB08A'}}>{!T.paidPeriods?'—':T.overpaid===0?'✓':T.overpaid>0?`${fmtAmt(T.overpaid)} over`:`${fmtAmt(-T.overpaid)} under`}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p style={{fontSize:11,color:'var(--text3)',margin:'10px 0 0',lineHeight:1.6}}>
                <strong style={{color:'var(--text2)'}}>Days off</strong> — working days not worked, whether it was approved leave or an absence. Each one comes out of the paid days off allowance automatically. Before records began, the number you type is the record.{' '}
                <strong style={{color:'var(--text2)'}}>+ / −</strong> — hours balance within each month only. Extra hours are kept on record but never make up for another month.{' '}
                <strong style={{color:'var(--text2)'}}>Unpaid</strong> — days off after the allowance ran out. These are cut from salary.{' '}
                <strong style={{color:'var(--text2)'}}>Difference</strong> — what was paid against what should have been paid, for salaries already recorded.
              </p>
            </div>
            );
          })()}

          {/* ── ATTENDANCE ── */}
          {profileTab==='attendance'&&(
            <div className="page-anim" style={{padding:'1.25rem 1.5rem'}}>
              <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:14}}>
                <Btn size="sm" variant="ghost" onClick={()=>{ if(calMonth===0){setCalMonth(11);setCalYear(y=>y-1);}else setCalMonth(m=>m-1); }}><ChevronLeft size={13}/></Btn>
                <span style={{fontWeight:600,fontSize:13,color:'var(--text)',minWidth:120,textAlign:'center',fontFamily:'var(--mono)',fontVariantNumeric:'lining-nums tabular-nums'}}>{MONTHS[calMonth]} {calYear}</span>
                <Btn size="sm" variant="ghost" onClick={()=>{ if(calMonth===11){setCalMonth(0);setCalYear(y=>y+1);}else setCalMonth(m=>m+1); }}><ChevronRight size={13}/></Btn>
                <span style={{fontSize:11,color:'var(--text3)'}}>Click to cycle: — → Present → Half → Absent → Leave</span>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(7,minmax(0,1fr))',gap:5,marginBottom:14}}>
                {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(d=><div key={d} style={{textAlign:'center',fontSize:10,fontWeight:400,fontFamily:'var(--mono)',color:'var(--text3)',textTransform:'uppercase',letterSpacing:'0.1em',padding:'3px 0'}}>{d}</div>)}
                {(()=>{ const fd=new Date(calYear,calMonth,1).getDay(); const off=fd===0?6:fd-1; return Array(off).fill(null).map((_,i)=><div key={'b'+i}/>); })()}
                {Array.from({length:new Date(calYear,calMonth+1,0).getDate()},(_,i)=>{
                  const day=i+1; const date=`${calYear}-${String(calMonth+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
                  const dow=new Date(calYear,calMonth,day).getDay();
                  const isWknd=dow===0||dow===6;
                  const a=getAtt(emp.id)[date]||'';
                  const bgMap={present:'var(--green-bg)',absent:'var(--red-bg)',half:'rgba(169,137,83,0.12)',leave:'var(--blue-bg)',''  :'var(--bg3)'};
                  const bMap ={present:'#8FB08A',  absent:'#C08079',    half:'var(--accent)',          leave:'var(--blue)',   ''  :'var(--border)'};
                  return <div key={date} onClick={()=>!isWknd&&cycleAtt(emp.id,date)} style={{borderRadius:'var(--radius)',padding:'7px 3px',textAlign:'center',fontSize:11,fontWeight:500,fontFamily:'var(--mono)',fontVariantNumeric:'lining-nums tabular-nums',background:isWknd?'transparent':bgMap[a],border:`1px solid ${isWknd?'transparent':bMap[a]}`,color:isWknd?'var(--text3)':'var(--text)',cursor:isWknd?'default':'pointer',opacity:isWknd?0.3:1}}>
                    <div>{day}</div>
                    {!isWknd&&a&&<div style={{fontSize:8,opacity:0.8,marginTop:1}}>{a==='present'?'✓':a==='absent'?'✗':a==='half'?'½':a==='leave'?'L':''}</div>}
                  </div>;
                })}
              </div>
              {(()=>{
                const att=getAtt(emp.id);
                const present=workingDays.filter(d=>att[d]==='present').length;
                const absent=workingDays.filter(d=>att[d]==='absent').length;
                const half=workingDays.filter(d=>att[d]==='half').length;
                const leave=workingDays.filter(d=>att[d]==='leave').length;
                const units=calcHolidayUnits(att,workingDays);
                return <div className="grid-att-summary">
                  {[['Present',present,'#8FB08A'],['Absent',absent,'#C08079'],['Half day',half,'var(--accent)'],['Leave',leave,'var(--blue)'],['Days off taken',units>0?units.toFixed(1):'0','var(--text3)']].map(([lbl,val,col])=>(
                    <div key={lbl} style={{background:'var(--bg3)',borderRadius:'var(--radius)',padding:'0.75rem'}}>
                      <div style={{fontSize:10,color:'var(--text3)',marginBottom:3,fontFamily:'var(--mono)',fontWeight:400,textTransform:'uppercase',letterSpacing:'0.1em'}}>{lbl}</div>
                      <div style={{fontSize:15,fontWeight:700,color:col,fontFamily:'var(--mono)',fontVariantNumeric:'lining-nums tabular-nums'}}>{val}</div>
                    </div>
                  ))}
                </div>;
              })()}
            </div>
          )}

          {/* ── TIME LOG ── */}
          {profileTab==='timelog'&&(
            <div className="page-anim" style={{padding:'1.25rem 1.5rem'}}>
              <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:14}}>
                <Btn size="sm" variant="ghost" onClick={()=>{ if(calMonth===0){setCalMonth(11);setCalYear(y=>y-1);}else setCalMonth(m=>m-1); }}><ChevronLeft size={13}/></Btn>
                <span style={{fontWeight:600,fontSize:13,color:'var(--text)',minWidth:120,textAlign:'center',fontFamily:'var(--mono)',fontVariantNumeric:'lining-nums tabular-nums'}}>{MONTHS[calMonth]} {calYear}</span>
                <Btn size="sm" variant="ghost" onClick={()=>{ if(calMonth===11){setCalMonth(0);setCalYear(y=>y+1);}else setCalMonth(m=>m+1); }}><ChevronRight size={13}/></Btn>
                <span style={{fontSize:11,color:'var(--text3)'}}>Admin can edit any time entry directly</span>
              </div>
              <div style={{overflowX:'auto'}}>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
                  <thead><tr style={{background:'var(--bg3)'}}>
                    {['Date','Required','Clock In','Clock Out','Hours worked'].map(h=>(
                      <th key={h} style={{padding:'8px 12px',textAlign:'left',fontSize:10,fontWeight:400,fontFamily:'var(--mono)',color:'var(--text3)',textTransform:'uppercase',letterSpacing:'0.06em',borderBottom:'1px solid var(--border)',whiteSpace:'nowrap'}}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {workingDays.map(date=>{
                      const entry=tl[date]||{};
                      const dailyScheduled = dailyMinsOf(wh);
                      const dailyWorked = (entry?.in && entry?.out) ? Math.max(0, toMins(entry.out) - toMins(entry.in)) : 0;
                      const hoursOk = dailyWorked >= dailyScheduled;
                      return <tr key={date} style={{borderBottom:'1px solid var(--border)'}}>
                        <td style={{padding:'7px 12px',color:'var(--text2)',fontSize:12,fontFamily:'var(--mono)',whiteSpace:'nowrap'}}>
                          {new Date(date+'T12:00:00').toLocaleDateString('default',{weekday:'short',day:'numeric',month:'short'})}
                        </td>
                        <td style={{padding:'7px 12px',color:'var(--text3)',fontSize:12,fontFamily:'var(--mono)'}}>{fmtMins(dailyScheduled)}</td>
                        <td style={{padding:'7px 12px'}}>
                          <input type="time" value={entry.in||''} onChange={e=>setTimeEntry(emp.id,date,'in',e.target.value)}
                            style={{background:'var(--bg3)',border:'1px solid var(--border2)',borderRadius:'var(--radius)',padding:'3px 7px',fontSize:12,color:'var(--text)',fontFamily:'var(--mono)',width:95}}/>
                        </td>
                        <td style={{padding:'7px 12px'}}>
                          <input type="time" value={entry.out||''} onChange={e=>setTimeEntry(emp.id,date,'out',e.target.value)}
                            style={{background:'var(--bg3)',border:`1px solid ${entry.autoOut&&entry.autoOut===entry.out?'#C79A5B':'var(--border2)'}`,borderRadius:'var(--radius)',padding:'3px 7px',fontSize:12,color:'var(--text)',fontFamily:'var(--mono)',width:95}}/>
                          {entry.autoOut&&entry.autoOut===entry.out&&<span title="They forgot to clock out; clocked out at their finish time at 23:00. Change the time to correct it." style={{fontSize:10,color:'#C79A5B',marginLeft:6}}>auto</span>}
                        </td>
                        <td style={{padding:'7px 12px',color:hoursOk?'#8FB08A':dailyWorked>0?'#C79A5B':'var(--text3)',fontSize:12,fontFamily:'var(--mono)'}}>{dailyWorked>0?fmtMins(dailyWorked):'—'}</td>

                      </tr>;
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── PAYROLL ── */}
          {profileTab==='payroll'&&(()=>{
            const st = PERIOD_STATUS[calc.status] || PERIOD_STATUS.in_progress;
            const isUpcoming = payKey === upcomingPayKey(emp, today);
            const needsAction = calc.flags.filter(f => FLAG_NEEDS_ACTION.has(f.type));
            const info = calc.flags.filter(f => !FLAG_NEEDS_ACTION.has(f.type));
            const decisionBadge = calc.deduction===0
              ? {label:'No deduction',color:'green'}
              : calc.waive ? {label:'Deduction waived', color:'blue'} : {label:'Deduction applies', color:'orange'};
            const plus = m => '+ '+fmtMins(m);
            const rows = [
              {label:'Base salary', value: fmtAmt(calc.base)+(calc.base!==calc.salary?` (pro-rated from ${fmtAmt(calc.salary)})`:''), color:'var(--text)'},
              // Owner's reading (2026-10-12): public holidays and days off are not required hours;
              // a day off goes on the days-off tracker only, so it is never ALSO hours missing.
              {label: `${calc.complete?'Hours required':'Hours required so far'} · ${Math.round((calc.daysElapsed-calc.daysOffTracked)*10)/10} day${calc.daysElapsed-calc.daysOffTracked===1?'':'s'} × ${fmtMins(calc.dailyMins)}`, value: fmtMins(calc.requiredMins), color:'var(--text3)'},
              calc.holidayDays>0 && {label:`Public holidays · ${calc.holidayDays} — not required`, value:'—', color:'var(--text3)'},
              calc.daysOffTracked>0 && {label:`Days off · ${calc.daysOffTracked} — not required, on the days-off tracker${calc.daysOffPaid?` (${calc.daysOffPaid} from the allowance)`:''}`, value:'—', color:'var(--blue)'},
              {label:'Clocked hours', value: fmtMins(calc.workedMins), color:'var(--text)'},
              calc.autoClosedMins>0 && {label:'Forgotten clock-outs, closed at shift end', value: plus(calc.autoClosedMins), color:'#C79A5B'},
              calc.adminMins>0      && {label:'Marked present by admin (no punch)', value: plus(calc.adminMins), color:'var(--text)'},
              calc.otCreditMins>0   && {label:'Approved overtime outside clock-ins', value: plus(calc.otCreditMins), color:'var(--accent)'},
              {label:'Hours worked', value: fmtMins(calc.hoursWorkedMins), color: calc.hoursWorkedMins>=calc.requiredMins?'#8FB08A':'#C79A5B', bold:true},
              {label:'Short hours', value: calc.hoursShortMins>0?`${fmtMins(calc.hoursShortMins)}${calc.cutForHours?` → − ${fmtAmt(calc.cutForHours)}`:''}`:'None', color: calc.hoursShortMins>0?'#C08079':'#8FB08A'},
              calc.cutForDaysOff>0 && {label:`Unpaid days off · ${calc.daysOffUnpaid} beyond the allowance (cut as days, not as hours)`, value:'− '+fmtAmt(calc.cutForDaysOff), color:'#C08079'},
              {label: calc.waive?'Deduction (waived by you)':'Deduction', value: calc.deduction>0?(calc.waive?fmtAmt(0)+' (was '+fmtAmt(calc.deduction)+')':'− '+fmtAmt(calc.deduction)):'—', color: calc.finalDed>0?'#C08079':'#8FB08A'},
              {label: calc.overtimeMins>0 ? `Overtime · ${fmtMins(calc.overtimeMins)} beyond the schedule × 1.5 (${fmtAmt(Math.round(calc.hourlyRate*1.5))}/h)` : 'Overtime (only hours beyond the schedule)', value: calc.overtimePay>0?'+ '+fmtAmt(calc.overtimePay):'—', color: calc.overtimePay>0?'var(--accent)':'var(--text3)'},
              {label: calc.complete?'Final salary due':'Final salary so far', value: fmtAmt(calc.finalSalary), color: calc.finalDed>0?'#C79A5B':'#8FB08A', bold:true},
            ].filter(Boolean);
            const dayName = ds => ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][new Date(ds+'T00:00:00Z').getUTCDay()];
            return (
            <div className="page-anim" style={{padding:'1.25rem 1.5rem'}}>
              {/* Period picker — salaries are keyed by the month they fall due */}
              <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:18,flexWrap:'wrap'}}>
                <Btn size="sm" variant="ghost" onClick={()=>setPayKey(prevPayKey(payKey))}><ChevronLeft size={13}/></Btn>
                <div style={{minWidth:190}}>
                  <div style={{fontSize:14,fontWeight:600,color:'var(--text)',fontFamily:'var(--mono)',fontVariantNumeric:'lining-nums tabular-nums'}}>{fmtPeriod(calc.period)}</div>
                  <div style={{fontSize:10,color:'var(--text3)',fontFamily:'var(--mono)',letterSpacing:'0.1em',textTransform:'uppercase',marginTop:2}}>Salary due {fmtDate(calc.period.dueDate)}</div>
                </div>
                <Btn size="sm" variant="ghost" onClick={()=>setPayKey(nextPayKey(payKey))}><ChevronRight size={13}/></Btn>
                <Badge label={st.label} color={st.color}/>
                {!isUpcoming && <Btn size="sm" variant="ghost" onClick={()=>setPayKey(upcomingPayKey(emp, today))}>Current period</Btn>}
              </div>

              <div className="grid-salary">
                <div>
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12,gap:10,flexWrap:'wrap'}}>
                    <p style={{fontSize:13,fontWeight:600,color:'var(--text)',margin:0}}>Calculation</p>
                    <Badge label={decisionBadge.label} color={decisionBadge.color}/>
                  </div>
                  {rows.map(row=>(
                    <div key={row.label} style={{display:'flex',justifyContent:'space-between',gap:10,padding:'8px 0',borderBottom:'1px solid var(--border)'}}>
                      <span style={{fontSize:13,color:'var(--text3)'}}>{row.label}</span>
                      <span style={{fontSize:13,fontWeight:row.bold?700:500,color:row.color,fontFamily:'var(--mono)',textAlign:'right',fontVariantNumeric:'lining-nums tabular-nums'}}>{row.value}</span>
                    </div>
                  ))}
                  {!calc.complete && <p style={{fontSize:11,color:'var(--text3)',margin:'10px 0 0'}}>This period is still running. Days that haven't happened yet are not counted as missed.</p>}
                </div>
                <div style={{display:'flex',flexDirection:'column',gap:12}}>
                  <p style={{fontSize:13,fontWeight:600,color:'var(--text)',margin:0}}>Decision &amp; controls</p>

                  <div style={{background:'var(--bg3)',borderRadius:'var(--radius)',padding:'12px 14px'}}>
                    <div style={{fontSize:13,fontWeight:600,color:'var(--text)',marginBottom:4}}>Days off this period</div>
                    <div style={{fontSize:20,fontWeight:700,color:calc.daysOffUnpaid>0?'#C08079':'var(--text)',fontFamily:'var(--mono)',fontVariantNumeric:'lining-nums tabular-nums'}}>
                      {calc.daysOff}{calc.daysOffUnpaid>0 && <span style={{fontSize:13,fontWeight:500}}> · {calc.daysOffUnpaid} unpaid</span>}
                    </div>
                    <div style={{fontSize:11,color:'var(--text3)',marginTop:4}}>
                      {calc.daysOff===0 ? 'None.' : `${calc.daysOffPaid} from the allowance${calc.daysOffBySalary?` · ${calc.daysOffBySalary} taken as a salary cut`:''}${calc.daysOffApproved?` · ${calc.daysOffApproved} with approved leave`:''}`} · {calc.pto.remaining} of {calc.pto.accrued} paid days off left after this period
                    </div>
                    <DaysOffChoices calc={calc} fmtDate={fmtDate} onChoose={(d,v)=>setOffChoice(emp.id, d, v)}/>
                  </div>

                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',background:'var(--bg3)',borderRadius:'var(--radius)',padding:'11px 13px'}}>
                    <div><div style={{fontSize:13,fontWeight:500,color:'var(--text)'}}>Waive deduction (override)</div><div style={{fontSize:11,color:'var(--text3)'}}>Pay full salary despite the shortfall</div></div>
                    <button onClick={()=>setPayrollDecision(emp.id, payKey, {waive:!calc.waive, decidedBy:'admin'})} style={{width:44,height:24,borderRadius:'var(--radius-lg)',border:'none',cursor:'pointer',background:calc.waive?'var(--accent)':'var(--bg4)',position:'relative',transition:'background 0.2s',flexShrink:0}}>
                      <div style={{position:'absolute',top:3,left:calc.waive?23:3,width:18,height:18,borderRadius:'50%',background:'var(--text)',transition:'left 0.2s'}}/>
                    </button>
                  </div>

                  <div style={{background:calc.finalDed>0?'var(--red-bg)':'var(--green-bg)',border:`1px solid ${calc.finalDed>0?'rgba(192,128,121,0.3)':'rgba(143,176,138,0.3)'}`,borderRadius:'var(--radius)',padding:14,textAlign:'center'}}>
                    <div style={{fontSize:11,color:'var(--text3)',marginBottom:4,fontFamily:'var(--mono)',fontWeight:400,textTransform:'uppercase',letterSpacing:'0.1em'}}>{calc.complete?'Final salary to pay':'Salary so far'}</div>
                    <div className="fig-md" style={{fontWeight:800,color:calc.finalDed>0?'#C08079':'#8FB08A',fontFamily:'var(--mono)'}}>{fmtAmt(calc.finalSalary)}</div>
                    {calc.finalDed>0&&<div style={{fontSize:11,color:'var(--text3)',marginTop:3}}>Deduction: {fmtAmt(calc.finalDed)}</div>}
                  </div>
                  {calc.paid && <p style={{fontSize:11,color:'#8FB08A',margin:0}}>This salary is recorded as paid. Changes here won't alter the payment already made.</p>}
                  <p style={{fontSize:11,color:'var(--text3)',margin:0}}>Every day off — approved leave or not — comes out of the paid days off allowance automatically. Once it runs out, each further day off is cut from salary.</p>
                </div>
              </div>

              {/* Records to fix before paying */}
              {needsAction.length>0 && (
                <div style={{marginTop:22,background:'rgba(199,154,91,0.08)',border:'1px solid rgba(199,154,91,0.3)',borderRadius:'var(--radius)',padding:'14px 16px'}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:10,marginBottom:10,flexWrap:'wrap'}}>
                    <div style={{fontSize:13,fontWeight:600,color:'#C79A5B'}}>{needsAction.length} record{needsAction.length===1?'':'s'} to check before paying</div>
                    <Btn size="sm" variant="ghost" onClick={()=>setProfileTab('timelog')}>Open time log</Btn>
                  </div>
                  {needsAction.map((f,i)=>(
                    <div key={f.date+f.type+i} style={{display:'flex',gap:12,padding:'6px 0',borderTop:i?'1px solid var(--border)':'none',fontSize:12.5}}>
                      <span style={{fontFamily:'var(--mono)',color:'var(--text2)',minWidth:84,fontVariantNumeric:'lining-nums tabular-nums'}}>{dayName(f.date)} {fmtDate(f.date)}</span>
                      <span style={{color:'var(--text2)'}}>{flagText(f)}</span>
                    </div>
                  ))}
                </div>
              )}
              {info.length>0 && (
                <div style={{marginTop:10,fontSize:12,color:'var(--text3)'}}>
                  {info.map((f,i)=><div key={f.date+i}>{dayName(f.date)} {fmtDate(f.date)} — {flagText(f)}</div>)}
                </div>
              )}

              {/* Leave request history */}
              {empLeaves.length>0&&(
                <div style={{marginTop:24}}>
                  <div style={{fontSize:12,fontWeight:400,fontFamily:'var(--mono)',color:'var(--text3)',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:8}}>Leave request history</div>
                  <div style={{background:'var(--bg3)',borderRadius:'var(--radius)',overflow:'hidden'}}>
                    {empLeaves.slice().sort((a,b)=>b.date.localeCompare(a.date)).map((r,i)=>(
                      <div key={r.id} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',borderBottom:i<empLeaves.length-1?'1px solid var(--border)':'none'}}>
                        <div style={{flex:1,minWidth:0}}>
                          <span style={{fontSize:12,color:'var(--text2)',fontFamily:'var(--mono)'}}>{r.date}</span>
                          <span style={{fontSize:12,color:'var(--text3)',marginLeft:10}}>{r.reason}</span>
                          {!r.onTime&&<span style={{fontSize:10,color:'#C79A5B',marginLeft:8,background:'rgba(199,154,91,0.12)',padding:'1px 6px',borderRadius:'var(--radius)'}}>Late</span>}
                        </div>
                        <span style={{fontSize:11,fontWeight:600,padding:'2px 8px',borderRadius:'var(--radius)',background:r.status==='approved'?'var(--green-bg)':r.status==='rejected'?'var(--red-bg)':'var(--bg4)',color:r.status==='approved'?'#8FB08A':r.status==='rejected'?'#C08079':'var(--text3)'}}>{r.status}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            );
          })()}

          {/* ── OVERTIME ── */}
          {profileTab==='overtime'&&(()=>{
            // Same engine, same period as the Payroll tab — the two can no longer disagree.
            const empOTReqs = (store.overtimeRequests||[]).filter(r=>r.empId===emp.id);
            const net = calc.hoursWorkedMins - calc.requiredMins;
            return (
              <div className="page-anim" style={{padding:'1.25rem 1.5rem'}}>
                <div style={{fontWeight:600,fontSize:13,color:'var(--text)',marginBottom:3}}>Hours · {fmtPeriod(calc.period)}</div>
                <div style={{fontSize:11,color:'var(--text3)',marginBottom:12}}>Same period as the Payroll tab. Overtime is paid only for hours beyond this month's schedule, at 1.5× the hourly rate ({fmtAmt(calc.hourlyRate)}/h → {fmtAmt(Math.round(calc.hourlyRate*1.5))}/h). Hours below the schedule — normal or overtime — just earn the salary. Weekend and holiday work needs an approved request.</div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:8,marginBottom:20}}>
                  {[
                    [calc.complete?'Required':'Required so far', fmtMins(calc.requiredMins), 'var(--text2)'],
                    ['Worked',       fmtMins(calc.hoursWorkedMins),  calc.hoursWorkedMins>=calc.requiredMins?'#8FB08A':'#C79A5B'],
                    ['Net position', net===0?'Even':(net>0?'+':'−')+fmtMins(Math.abs(net)), net>=0?'#8FB08A':'#C08079'],
                    ['Overtime pay', calc.overtimePay>0?fmtAmt(calc.overtimePay):'—', 'var(--accent)'],
                  ].map(([l,v,col])=>(
                    <div key={l} style={{background:'var(--bg3)',borderRadius:'var(--radius)',padding:'10px 12px',textAlign:'center'}}>
                      <div style={{fontSize:9,color:'var(--text3)',fontWeight:400,fontFamily:'var(--mono)',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:4}}>{l}</div>
                      <div style={{fontSize:15,fontWeight:700,color:col,fontFamily:'var(--mono)',fontVariantNumeric:'lining-nums tabular-nums'}}>{v}</div>
                    </div>
                  ))}
                </div>
                <div style={{background:'var(--bg3)',borderRadius:'var(--radius)',padding:'12px 14px',marginBottom:20,fontSize:13,display:'flex',justifyContent:'space-between',alignItems:'center',gap:12,flexWrap:'wrap'}}>
                  <span style={{color:'var(--text3)'}}>Effect on the salary due {fmtDate(calc.period.dueDate)}</span>
                  <span style={{fontFamily:'var(--mono)',fontWeight:700,color:calc.finalDed>0?'#C08079':calc.overtimePay>0?'var(--accent)':'#8FB08A',fontVariantNumeric:'lining-nums tabular-nums'}}>{calc.finalDed>0?`− ${fmtAmt(calc.finalDed)} → ${fmtAmt(calc.finalSalary)}`:calc.overtimePay>0?`+ ${fmtAmt(calc.overtimePay)} overtime → ${fmtAmt(calc.finalSalary)}`:`No deduction · ${fmtAmt(calc.finalSalary)}`}</span>
                </div>
                {/* OT requests for this employee */}
                <div style={{fontWeight:600,fontSize:13,color:'var(--text)',marginBottom:10}}>Overtime requests</div>
                {empOTReqs.length===0
                  ? <div style={{fontSize:13,color:'var(--text3)'}}>No overtime requests from this employee.</div>
                  : empOTReqs.slice().reverse().map(r=>{
                    const mins = r.startTime&&r.endTime ? (parseInt(r.endTime.split(':')[0])*60+parseInt(r.endTime.split(':')[1])) - (parseInt(r.startTime.split(':')[0])*60+parseInt(r.startTime.split(':')[1])) : 0;
                    return (
                      <div key={r.id} style={{display:'flex',alignItems:'flex-start',gap:10,padding:'10px 0',borderBottom:'1px solid var(--border)'}}>
                        <div style={{flex:1}}>
                          <div style={{fontWeight:500,fontSize:13,color:'var(--text)',marginBottom:2,fontFamily:'var(--mono)',fontVariantNumeric:'lining-nums tabular-nums'}}>{r.date} · {r.startTime} – {r.endTime} <span style={{color:'var(--accent)',fontWeight:600}}>({fmtMins(mins)})</span></div>
                          <div style={{fontSize:12,color:'var(--text3)'}}>{r.reason}</div>
                          {r.status==='approved'&&mins>0&&(<div style={{fontSize:11,color:'#8FB08A',marginTop:2}}>
                            Counts toward this month's hours; paid at 1.5× only if the month goes beyond its schedule
                          </div>)}
                        </div>
                        {r.status==='pending'
                          ? <div style={{display:'flex',gap:6,flexShrink:0}}>
                              <Btn size="sm" variant="primary" onClick={()=>resolveOvertime(r.id,'approved')}>Approve</Btn>
                              <Btn size="sm" variant="danger"  onClick={()=>resolveOvertime(r.id,'rejected')}>Reject</Btn>
                            </div>
                          : <span style={{fontSize:11,fontWeight:600,padding:'3px 10px',borderRadius:'var(--radius)',background:r.status==='approved'?'var(--green-bg)':'var(--red-bg)',color:r.status==='approved'?'#8FB08A':'#C08079',flexShrink:0}}>
                              {r.status.charAt(0).toUpperCase()+r.status.slice(1)}
                            </span>
                        }
                      </div>
                    );
                  })
                }
              </div>
            );
          })()}

          {/* ── PERMISSIONS ── */}
          {profileTab==='permissions'&&(
            <div className="page-anim" style={{padding:'1.25rem 1.5rem'}}>
              <div style={{fontWeight:600,fontSize:13,color:'var(--text)',marginBottom:6}}>Report access permissions</div>
              <p style={{fontSize:12,color:'var(--text3)',marginBottom:20}}>Control which report types this employee can view in their portal. Changes take effect immediately on their next login.</p>
              <div style={{display:'flex',flexDirection:'column',gap:10,maxWidth:440}}>
                {[
                  {key:'socialMedia',label:'Social Media Reports',desc:'Monthly social media performance reports for clients'},
                  {key:'perfMarketing',label:'Performance Marketing Reports',desc:'Ad spend, ROAS, CPL and campaign-level data'},
                ].map(({key,label,desc})=>{
                  const active = emp.reportPerms?.[key]||false;
                  return (
                    <div key={key} style={{display:'flex',alignItems:'center',justifyContent:'space-between',background:'var(--bg3)',borderRadius:'var(--radius)',padding:'12px 14px'}}>
                      <div>
                        <div style={{fontSize:13,fontWeight:500,color:'var(--text)',marginBottom:2}}>{label}</div>
                        <div style={{fontSize:11,color:'var(--text3)'}}>{desc}</div>
                      </div>
                      <button onClick={()=>toggleReportPerm(emp.id,key)} style={{width:44,height:24,borderRadius:'var(--radius-lg)',border:'none',cursor:'pointer',background:active?'var(--accent)':'var(--bg4)',position:'relative',transition:'background 0.2s',flexShrink:0,marginLeft:16}}>
                        <div style={{position:'absolute',top:3,left:active?23:3,width:18,height:18,borderRadius:'50%',background:'var(--text)',transition:'left 0.2s'}}/>
                      </button>
                    </div>
                  );
                })}
              </div>
              <div style={{marginTop:16,padding:'10px 12px',background:'var(--bg3)',borderRadius:'var(--radius)',fontSize:12,color:'var(--text3)'}}>
                Current role: <strong style={{color:'var(--text)'}}>{emp.role||'No role assigned'}</strong>
                {emp.role && ` — default permissions for this role: ${ROLE_DEFAULT_PERMISSIONS[emp.role]?.socialMedia?'Social Media ':''}${ROLE_DEFAULT_PERMISSIONS[emp.role]?.perfMarketing?'Performance Marketing':''||'No reports'}`}
              </div>
            </div>
          )}

          {/* ── CREDENTIALS ── */}
          {profileTab==='creds'&&(
            <div className="page-anim" style={{padding:'1.25rem 1.5rem'}}>
              {empCred?(
                <div style={{background:'var(--bg3)',borderRadius:'var(--radius-lg)',padding:'1.25rem',maxWidth:400}}>
                  <div style={{fontSize:13,fontWeight:600,color:'var(--text)',marginBottom:14}}>Login credentials</div>
                  {[['Username',empCred.username,'user']].map(([lbl,val,field])=>(
                    <div key={lbl} style={{marginBottom:12}}>
                      <label style={{fontSize:11,fontWeight:400,fontFamily:'var(--mono)',color:'var(--text3)',textTransform:'uppercase',letterSpacing:'0.06em',display:'block',marginBottom:5}}>{lbl}</label>
                      <div style={{display:'flex',alignItems:'center',gap:8}}>
                        <div style={{flex:1,background:'var(--bg4)',border:'1px solid var(--border)',borderRadius:'var(--radius)',padding:'8px 12px',fontFamily:'var(--mono)',fontSize:13,color:'var(--text)'}}>{val}</div>
                        <button onClick={()=>copyText(val,field+emp.id)} style={{background:'var(--bg4)',border:'1px solid var(--border)',borderRadius:'var(--radius)',padding:7,cursor:'pointer',color:copiedField===field+emp.id?'#8FB08A':'var(--text3)',display:'flex'}}>
                          {copiedField===field+emp.id?<Check size={14}/>:<Copy size={14}/>}
                        </button>
                      </div>
                    </div>
                  ))}
                  <div style={{marginTop:14,padding:'10px 12px',background:'rgba(199,154,91,0.08)',border:'1px solid rgba(199,154,91,0.2)',borderRadius:'var(--radius)',fontSize:12,color:'#C79A5B'}}>
                    Passwords are checked by the server and aren't stored in the app, so nobody (including admins) can look one up. If {emp.name.split(' ')[0]} forgets theirs, set a new one and share it securely.
                  </div>
                  <div style={{marginTop:10}}>
                    <Btn size="sm" variant="ghost" onClick={()=>{
                      if (window.confirm(`Set a new password for ${emp.name}? Their current password will stop working.`)) issuePassword(emp, empCred.username);
                    }}><RefreshCw size={12}/>Set a new password</Btn>
                  </div>
                </div>
              ):<div style={{color:'var(--text3)',fontSize:13,padding:'1rem'}}>
                  No sign-in yet for {emp.name}.
                  <div style={{marginTop:10}}><Btn size="sm" variant="primary" onClick={()=>issuePassword(emp, null)}>Create a sign-in</Btn></div>
                </div>}
            </div>
          )}
        </Card>

        <EmpModal open={showEmpModal} onClose={()=>setShowEmpModal(false)} form={empForm} setForm={setEmpForm} onSave={saveEmp} editing={!!editingEmp}/>
      </div>
    );
  }

  // ── LIST VIEW ─────────────────────────────────────────────────────
  const pendingLeaves = leaves.filter(r=>r.status==='pending');

  return (
    <div style={{display:'flex',flexDirection:'column',gap:20}}>
      {/* Page header */}
      <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:16,flexWrap:'wrap'}}>
        <div>
          <div className="t-label" style={{color:'var(--accent2)',marginBottom:6}}>HR Management · Personnel Directory</div>
          <h1 className="t-h1">Resource Management</h1>
        </div>
        <Btn variant="primary" onClick={openAdd}><Plus size={15}/> Add Employee</Btn>
      </div>

      {/* Employee photo cards */}
      {employees.length===0?<Card><EmptyState message="No employees yet."/></Card>:(
        <div className="grid-hr-cards stagger">
          {employees.map(emp=>{
            const h=getHR(emp.id); const noPay=!onPayroll(store, emp); const calc=getSalaryCalc(emp);
            // Status of this person's own pay cycle (due days differ), not the calendar month.
            const st=PERIOD_STATUS[calc.status]||PERIOD_STATUS.in_progress;
            const tone={green:['#8FB08A','var(--green-bg)'],orange:['#C79A5B','var(--orange-bg)'],red:['#C08079','var(--red-bg)'],blue:['var(--blue)','var(--blue-bg)'],gray:['var(--text3)','var(--bg4)']}[st.color];
            const bankLeft=leaveBalance(store, emp, today).remaining;
            const role=emp.role||'';
            const roleClass = role.includes('Video')?'role-video':role.includes('Graphic')?'role-graphic':role.includes('Content')?'role-content':role.includes('Schedul')||role.includes('Strateg')?'role-strategist':'';
            return (
              <Card key={emp.id} hover className="fx-card" style={{padding:'22px',display:'flex',flexDirection:'column',alignItems:'center',textAlign:'center'}}>
                <div style={{alignSelf:'stretch',display:'flex',justifyContent:'flex-end',marginBottom:-8,visibility:noPay?'hidden':'visible'}}>
                  <span style={{fontFamily:'var(--mono)',fontSize:10,fontWeight:400,letterSpacing:'0.1em',textTransform:'uppercase',color:tone[0],background:tone[1],padding:'3px 9px',borderRadius:'var(--radius)'}} title={`Salary due ${fmtDate(calc.period.dueDate)}`}>{st.label}</span>
                </div>
                <div style={{width:76,height:76,borderRadius:'50%',background:'var(--bg3)',border:'1px solid var(--border)',overflow:'hidden',display:'flex',alignItems:'center',justifyContent:'center',marginBottom:14}}>
                  {h.photo?<img src={h.photo.data} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/>:<User size={30} color="var(--text3)"/>}
                </div>
                <div style={{fontSize:17,fontWeight:400,fontFamily:'var(--serif)',color:'var(--text)',letterSpacing:'-0.01em'}}>{emp.name}</div>
                <div style={{fontSize:13,color:'var(--text3)',marginTop:2,marginBottom:16}}>{role||'—'}</div>
                <div style={{alignSelf:'stretch',borderTop:'1px solid var(--border)',paddingTop:14,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                  {noPay ? <div style={{textAlign:'left',fontSize:12,color:'var(--text3)'}}>No hours or salary</div> : <div style={{textAlign:'left'}}>
                    <div className="t-label" style={{marginBottom:3}}>Salary</div>
                    <div className="t-currency" style={{fontSize:15,color:'var(--text)'}}>{fmtAmt(emp.amount)}<span style={{fontSize:11,color:'var(--text3)',fontWeight:500}}>/mo</span></div>
                  </div>}
                  {roleClass && <span className={`role-pill ${roleClass}`}>{role}</span>}
                </div>
                {noPay ? null : calc.finalDed>0
                  ? <div style={{alignSelf:'stretch',marginTop:10}}><Badge label={`− ${fmtAmt(calc.finalDed)} · due ${fmtDate(calc.period.dueDate)}`} color="red"/></div>
                  : bankLeft>0 && <div style={{alignSelf:'stretch',marginTop:10}}><Badge label={`${bankLeft} paid days off left`} color="blue"/></div>}
                <div style={{display:'flex',gap:6,marginTop:16,alignSelf:'stretch'}}>
                  <Btn size="sm" onClick={()=>{setSelectedEmp(emp);setView('profile');setProfileTab('overview');}} style={{flex:1}}>Profile</Btn>
                  <Btn size="sm" variant="default" onClick={()=>openEdit(emp)}>Edit</Btn>
                  <Btn size="sm" variant="danger" onClick={()=>deleteEmp(emp.id)}>×</Btn>
                </div>
              </Card>
            );
          })}
          {/* Add new member tile */}
          <button onClick={openAdd} style={{border:'2px dashed var(--border2)',borderRadius:'var(--radius-lg)',background:'transparent',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:10,minHeight:240,cursor:'pointer',color:'var(--text3)',transition:'.18s',fontFamily:'var(--font)'}}
            onMouseEnter={e=>{e.currentTarget.style.borderColor='var(--accent)';e.currentTarget.style.color='var(--accent2)';}}
            onMouseLeave={e=>{e.currentTarget.style.borderColor='var(--border2)';e.currentTarget.style.color='var(--text3)';}}>
            <div style={{width:46,height:46,borderRadius:'50%',border:'2px solid currentColor',display:'flex',alignItems:'center',justifyContent:'center'}}><Plus size={22}/></div>
            <span style={{fontSize:13,fontWeight:600,letterSpacing:'0.04em',textTransform:'uppercase',fontFamily:'var(--font-ui)'}}>Add New Member</span>
          </button>
        </div>
      )}

      {/* Team year summary — the same records as each person's Year record tab */}
      {employees.length>0 && (()=>{
        const rows = employees.filter(e=>onPayroll(store, e)).map(e=>({ e, t: teamRecords[e.id]?.totals })).filter(x=>x.t);
        const sum = f => rows.reduce((a,x)=>a+(Number(f(x.t))||0),0);
        const td = {padding:'10px 12px',borderBottom:'1px solid var(--border)',fontFamily:'var(--mono)',fontSize:12.5,fontVariantNumeric:'lining-nums tabular-nums',textAlign:'right',whiteSpace:'nowrap'};
        const th = {...td,fontSize:9.5,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'0.08em',fontWeight:400};
        const hrs = m => fmtMins(Math.abs(m))==='—'?'0h':fmtMins(Math.abs(m));
        const over = n => n===0?'✓':n>0?`${fmtAmt(n)} over`:`${fmtAmt(-n)} under`;
        return (
          <Card style={{padding:0,overflow:'hidden'}}>
            <div style={{padding:'16px 20px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
              <span style={{fontFamily:'var(--serif)',fontSize:18,color:'var(--text)'}}>Team record</span>
              <Btn size="sm" variant="ghost" onClick={()=>setRecordYear(y=>y-1)}><ChevronLeft size={13}/></Btn>
              <span style={{fontFamily:'var(--mono)',fontSize:13,color:'var(--text)'}}>{recordYear}</span>
              <Btn size="sm" variant="ghost" onClick={()=>setRecordYear(y=>y+1)}><ChevronRight size={13}/></Btn>
              <span style={{fontSize:12,color:'var(--text3)'}}>Click a name for the month-by-month record</span>
            </div>
            <div style={{overflowX:'auto'}}>
              <table style={{width:'100%',borderCollapse:'collapse',minWidth:1020}}>
                <thead><tr>
                  <th style={{...th,textAlign:'left'}}>Employee</th><th style={th}>Days off</th><th style={th}>Unpaid</th><th style={th}>Paid days off left</th>
                  <th style={th}>Hours a month</th><th style={th}>Months short</th><th style={th}>Avg day</th>
                  <th style={th}>Should have cut</th><th style={th}>Overtime</th><th style={th}>Paid</th><th style={th}>Paid vs should</th>
                </tr></thead>
                <tbody>
                  {rows.map(({e,t})=>(
                    <tr key={e.id} onClick={()=>{setSelectedEmp(e);setView('profile');setProfileTab('record');}} style={{cursor:'pointer'}}>
                      <td style={{...td,textAlign:'left',fontFamily:'var(--font)',fontSize:13.5,color:'var(--text)'}}>{e.name}</td>
                      <td style={td}>{t.daysOff}</td>
                      <td style={{...td,color:t.daysOffUnpaid?'#C08079':'var(--text3)'}}>{t.daysOffUnpaid||'—'}</td>
                      <td style={{...td,color:t.bank.remaining>0?'var(--blue)':'#C08079'}}>{t.bank.remaining}</td>
                      <td style={{...td,color:t.avg.hoursWorkedMins<t.avg.requiredMins?'#C79A5B':'#8FB08A'}}>{hrs(t.avg.hoursWorkedMins)} / {hrs(t.avg.requiredMins)}</td>
                      <td style={td}>{t.complete?`${t.shortMonths} of ${t.complete}`:'—'}</td>
                      <td style={td}>{t.avg.hoursPerDay?hrs(t.avg.hoursPerDay):'—'}</td>
                      <td style={{...td,color:t.shouldCut?'#C08079':'var(--text3)'}}>{t.shouldCut?fmtAmt(t.shouldCut):'—'}</td>
                      <td style={{...td,color:t.overtimePay?'var(--accent)':'var(--text3)'}}>{t.overtimePay?fmtAmt(t.overtimePay):'—'}</td>
                      <td style={td}>{t.paidPeriods?fmtAmt(t.paid):'—'}</td>
                      <td style={{...td,color:t.overpaid>0?'#C08079':'#8FB08A'}}>{t.paidPeriods?over(t.overpaid):'—'}</td>
                    </tr>
                  ))}
                  <tr style={{background:'var(--bg3)'}}>
                    <td style={{...td,textAlign:'left',fontFamily:'var(--font)',fontWeight:600,color:'var(--text)'}}>Team</td>
                    <td style={{...td,fontWeight:600}}>{Math.round(sum(t=>t.daysOff)*10)/10}</td>
                    <td style={{...td,fontWeight:600}}>{Math.round(sum(t=>t.daysOffUnpaid)*10)/10||'—'}</td>
                    <td style={td}></td><td style={td}></td><td style={td}></td>
                    <td style={td}></td>
                    <td style={{...td,fontWeight:600,color:'#C08079'}}>{fmtAmt(sum(t=>t.shouldCut))}</td>
                    <td style={{...td,fontWeight:600,color:'var(--accent)'}}>{fmtAmt(sum(t=>t.overtimePay))}</td>
                    <td style={{...td,fontWeight:600}}>{fmtAmt(sum(t=>t.paid))}</td>
                    <td style={{...td,fontWeight:600,color:sum(t=>t.overpaid)>0?'#C08079':'#8FB08A'}}>{over(sum(t=>t.overpaid))}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Card>
        );
      })()}

      {/* Leave requests */}
      <Card>
        <SectionHeader title="Leave requests" count={pendingLeaves.length>0?pendingLeaves.length:undefined} action={
          <div style={{display:'flex',gap:6}}>
            {['pending','all'].map(t=><button key={t} onClick={()=>setLeaveTab(t)} style={{fontSize:12,fontWeight:500,padding:'4px 12px',borderRadius:'var(--radius)',cursor:'pointer',border:leaveTab===t?'1.5px solid var(--accent)':'1px solid var(--border2)',background:leaveTab===t?'rgba(169,137,83,0.1)':'transparent',color:leaveTab===t?'var(--accent)':'var(--text3)'}}>{t.charAt(0).toUpperCase()+t.slice(1)}</button>)}
          </div>
        }/>
        {(()=>{
          const show=leaveTab==='pending'?pendingLeaves:leaves;
          if (show.length===0) return <EmptyState message={leaveTab==='pending'?'No pending requests.':'No leave requests yet.'}/>;
          return <Table headers={['Employee','Date','Type','Reason','Submitted','On time','Status','Actions']}>
            {show.slice().sort((a,b)=>b.requestedAt?.localeCompare(a.requestedAt)).map(r=>{
              const emp=employees.find(e=>e.id===r.empId);
              return <TR key={r.id}>
                <TD bold>{r.empName||emp?.name||'—'}</TD>
                <TD mono>{r.date}</TD>
                <TD><Badge label={r.type==='full'?'Full day':'Half day'} color="gray"/></TD>
                <TD muted style={{maxWidth:180}}><span style={{fontSize:12,display:'block',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{r.reason}</span></TD>
                <TD muted style={{fontSize:11,fontFamily:'var(--mono)'}}>{r.requestedAt?new Date(r.requestedAt).toLocaleDateString():'—'}</TD>
                <TD>{r.onTime?<Badge label="On time" color="green"/>:<Badge label="Late" color="orange"/>}</TD>
                <TD><StatusBadge status={r.status==='pending'?'pending':r.status==='approved'?'paid':'pending'}/></TD>
                <TD>
                  {r.status==='pending'?(
                    <div style={{display:'flex',gap:5,flexWrap:'wrap'}}>
                      <Btn size="sm" variant="success" onClick={()=>handleLeave(r.id,'approved')}>Approve</Btn>
                      <Btn size="sm" variant="danger" onClick={()=>handleLeave(r.id,'rejected')}>Reject</Btn>
                    </div>
                  ):(
                    <div style={{fontSize:12,color:'var(--text3)'}}>
                      {r.status==='approved'?'✅ Approved':'❌ Rejected'}
                    </div>
                  )}
                </TD>
              </TR>;
            })}
          </Table>;
        })()}
      </Card>

      {/* Add/Edit Employee Modal */}
      <EmpModal open={showEmpModal} onClose={()=>setShowEmpModal(false)} form={empForm} setForm={setEmpForm} onSave={saveEmp} editing={!!editingEmp}/>

      {/* New credentials modal */}
      <Modal open={!!newCredModal} onClose={()=>setNewCredModal(null)} title={newCredModal?.reset ? 'New password set' : 'Sign-in created'}>
        {newCredModal&&(
          <>
            <div style={{background:'var(--green-bg)',border:'1px solid rgba(143,176,138,0.3)',borderRadius:'var(--radius)',padding:'12px 14px',marginBottom:16,fontSize:13,color:'#8FB08A',fontWeight:500}}>
              ✅ {newCredModal.reset ? `${newCredModal.name}'s password has been changed. Share the new one with them.` : `${newCredModal.name} can now sign in. Share these details with them.`}
            </div>
            {[['Username',newCredModal.username,'user'],['Password',newCredModal.password,'pass']].map(([lbl,val,field])=>(
              <div key={lbl} style={{marginBottom:12}}>
                <label style={{fontSize:11,fontWeight:400,fontFamily:'var(--mono)',color:'var(--text3)',textTransform:'uppercase',letterSpacing:'0.06em',display:'block',marginBottom:5}}>{lbl}</label>
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <div style={{flex:1,background:'var(--bg3)',border:'1px solid var(--border2)',borderRadius:'var(--radius)',padding:'9px 12px',fontFamily:'var(--mono)',fontSize:14,color:'var(--text)',letterSpacing:'0.02em'}}>{val}</div>
                  <button onClick={()=>copyText(val,'new'+field)} style={{background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:'var(--radius)',padding:8,cursor:'pointer',color:copiedField==='new'+field?'#8FB08A':'var(--text3)',display:'flex'}}>
                    {copiedField==='new'+field?<Check size={14}/>:<Copy size={14}/>}
                  </button>
                </div>
              </div>
            ))}
            <div style={{fontSize:12,color:'var(--text3)',marginTop:8}}>Copy the password now — for security it isn't stored anywhere, so it can't be shown again. You can always set a new one from the Credentials tab.</div>
            <div style={{display:'flex',justifyContent:'flex-end',marginTop:16}}>
              <Btn variant="primary" onClick={()=>setNewCredModal(null)}>Done</Btn>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}

function EmpModal({open,onClose,form,setForm,onSave,editing}) {
  const f=v=>e=>setForm(p=>({...p,[v]:e.target.value}));
  return (
    <Modal open={open} onClose={onClose} title={editing?'Edit employee':'Add employee'}>
      <Field label="Full name"><Input value={form.name||''} onChange={f('name')} placeholder="e.g. Ahmed Ali" autoFocus/></Field>
      <Field label="Role">
        <select value={form.role||'Content Manager'} onChange={f('role')} style={{width:'100%',padding:'9px 12px',fontSize:13,background:'var(--bg3)',border:'1px solid var(--border2)',borderRadius:'var(--radius)',color:'var(--text)',boxSizing:'border-box'}}>
          {EMPLOYEE_ROLES.map(r=><option key={r} value={r}>{r}</option>)}
        </select>
      </Field>
      <label style={{display:'flex',alignItems:'flex-start',gap:9,background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:'var(--radius)',padding:'10px 12px',marginBottom:12,cursor:'pointer'}}>
        <input type="checkbox" checked={!!form.noPayroll} onChange={e=>setForm(p=>({...p,noPayroll:e.target.checked}))} style={{marginTop:2,accentColor:'var(--accent)'}}/>
        <span style={{fontSize:12.5,color:'var(--text2)'}}><strong style={{color:'var(--text)'}}>No working hours or salary</strong> — e.g. an AI agent or a freelancer. They get a login and work access only, and are left out of payroll, attendance, hours and payday reminders.</span>
      </label>
      {!form.noPayroll && <ModalRow>
        <Field label="Monthly salary (PKR)"><Input type="number" value={form.amount||''} onChange={f('amount')} placeholder="e.g. 50000"/></Field>
        <Field label="Salary due day"><Input type="number" min={1} max={28} value={form.dueDay||''} onChange={f('dueDay')} placeholder="e.g. 1"/></Field>
      </ModalRow>}
      <Field label="Joining date"><Input type="date" value={form.joiningDate||''} onChange={f('joiningDate')}/></Field>
      {!form.noPayroll && <ModalRow>
        <Field label="Work start"><Input type="time" value={form.workStart||'09:00'} onChange={f('workStart')}/></Field>
        <Field label="Work end"><Input type="time" value={form.workEnd||'18:00'} onChange={f('workEnd')}/></Field>
      </ModalRow>}
      {editing && !form.noPayroll && (Number(form.amount)!==form._origAmount || form.workStart!==form._origStart || form.workEnd!==form._origEnd) && (
        <Field label="These changes apply">
          <select value={form.effective||'upcoming'} onChange={f('effective')} style={{width:'100%',padding:'9px 12px',fontSize:13,background:'var(--bg3)',border:'1px solid var(--border2)',borderRadius:'var(--radius)',color:'var(--text)',boxSizing:'border-box'}}>
            <option value="upcoming">From the salary due {form._fromLabel} onward — past months keep what was agreed then</option>
            <option value="all">To all past records too — the old figure was a mistake</option>
          </select>
        </Field>
      )}
      {form.role && (
        <div style={{background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:'var(--radius)',padding:'9px 12px',fontSize:12,color:'var(--text3)',marginTop:4}}>
          Default report access for <strong style={{color:'var(--text)'}}>{form.role}</strong>:{' '}
          {ROLE_DEFAULT_PERMISSIONS[form.role]?.socialMedia && <span style={{color:'#8FB08A',marginRight:6}}>✓ Social Media</span>}
          {ROLE_DEFAULT_PERMISSIONS[form.role]?.perfMarketing && <span style={{color:'#8FB08A'}}>✓ Performance Marketing</span>}
          {!ROLE_DEFAULT_PERMISSIONS[form.role]?.socialMedia && !ROLE_DEFAULT_PERMISSIONS[form.role]?.perfMarketing && <span>No reports (adjustable in Permissions tab)</span>}
        </div>
      )}
      {!editing&&<div style={{background:'var(--blue-bg)',border:'1px solid rgba(96,165,250,0.2)',borderRadius:'var(--radius)',padding:'10px 12px',fontSize:12,color:'var(--blue)',marginTop:8}}>Username and password will be auto-generated and displayed after saving.</div>}
      <div style={{display:'flex',justifyContent:'flex-end',gap:8,marginTop:'1.25rem'}}>
        <Btn onClick={onClose}>Cancel</Btn>
        <Btn variant="primary" onClick={onSave}>Save employee</Btn>
      </div>
    </Modal>
  );
}
